import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../utils/api';

function t(field, lang = 'en') {
  if (!field) return '';
  return field[lang] || field.en || '';
}

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [lang, setLang] = useState('en');

  useEffect(() => {
    const savedLang = localStorage.getItem('ets_lang');
    if (savedLang) setLang(savedLang);
  }, []);

  const content = {
    titleVisual: { en: 'Account\nRecovery', zh: '帳號\n救援' },
    descVisual: { en: 'Enter your registered email address and we\'ll send you a secure link to reset your password.', zh: '輸入您的註冊電子郵件地址，我們將發送安全連結供您重設密碼。' },
    checkEmail: { en: 'Check Your Email', zh: '檢查您的電子郵件' },
    ifRegistered: { en: 'If ', zh: '如果 ' },
    isRegistered: { en: ' is registered, a password reset link has been sent.', zh: ' 已註冊，我們已發送密碼重設連結。' },
    expiresIn: { en: 'The link expires in ', zh: '該連結將於 ' },
    fifteenMin: { en: '15 minutes', zh: '15 分鐘' },
    checkSpam: { en: '. Check your spam folder if you don\'t see it.', zh: '後失效。如果您沒有看到，請檢查您的垃圾郵件信箱。' },
    backToLogin: { en: '← Back to Login', zh: '← 返回登入' },
    forgotTitle: { en: 'Forgot Password?', zh: '忘記密碼？' },
    forgotDesc: { en: 'Enter your email and we\'ll send a reset link.', zh: '輸入您的電子郵件，我們將發送重設連結。' },
    emailLabel: { en: 'Registered Email Address', zh: '註冊的電子郵件地址' },
    sending: { en: 'Sending Reset Link...', zh: '發送重設連結中...' },
    sendLink: { en: 'Send Reset Link →', zh: '發送重設連結 →' },
    errEmpty: { en: 'Please enter your email address.', zh: '請輸入您的電子郵件地址。' },
    errGeneric: { en: 'Something went wrong. Please try again.', zh: '發生錯誤。請再試一次。' }
  };

  const titleLines = t(content.titleVisual, lang).split('\n');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return setError(t(content.errEmpty, lang));
    setLoading(true);
    setError('');
    try {
      await authAPI.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || t(content.errGeneric, lang));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Visual Panel */}
      <div className="login-visual" style={{
        background: 'linear-gradient(rgba(123, 29, 58, 0.75), rgba(17, 17, 17, 0.85)), url("/assets/login-bg.jpeg") center/cover no-repeat',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '60px', color: '#fff', position: 'relative'
      }}>
        {/* Language Toggle in top left of visual panel */}
        <div style={{ position: 'absolute', top: 40, left: 60, display: 'flex', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 20, padding: 4, backdropFilter: 'blur(5px)' }}>
          <button 
            onClick={() => { setLang('en'); localStorage.setItem('ets_lang', 'en'); }}
            style={{ background: lang === 'en' ? '#c04060' : 'transparent', color: '#fff', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
          >EN</button>
          <button 
            onClick={() => { setLang('zh'); localStorage.setItem('ets_lang', 'zh'); }}
            style={{ background: lang === 'zh' ? '#c04060' : 'transparent', color: '#fff', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
          >繁中</button>
        </div>

        <div style={{ marginBottom: 40, marginTop: 40 }}>
          <img src="/assets/logo.png" alt="ETS" style={{ width: 60, height: 60, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', marginBottom: 20 }} />
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 700, lineHeight: 1.1 }}>
            {titleLines[0]}<br />{titleLines[1]}
          </h1>
        </div>
        <p style={{ fontSize: 18, opacity: 0.7, maxWidth: 400, lineHeight: 1.6 }}>
          {t(content.descVisual, lang)}
        </p>
      </div>

      {/* Form Panel */}
      <div className="login-form-side" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {/* Language Toggle for mobile view (when visual panel is hidden) */}
        <div className="mobile-lang-toggle" style={{ position: 'absolute', top: 20, right: 20, display: 'flex', backgroundColor: '#f0f0f0', borderRadius: 20, padding: 4 }}>
          <button 
            onClick={() => { setLang('en'); localStorage.setItem('ets_lang', 'en'); }}
            style={{ background: lang === 'en' ? '#c04060' : 'transparent', color: lang === 'en' ? '#fff' : '#666', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
          >EN</button>
          <button 
            onClick={() => { setLang('zh'); localStorage.setItem('ets_lang', 'zh'); }}
            style={{ background: lang === 'zh' ? '#c04060' : 'transparent', color: lang === 'zh' ? '#fff' : '#666', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
          >繁中</button>
        </div>
        <style>{`
          .mobile-lang-toggle { display: none !important; }
          @media (max-width: 900px) {
            .mobile-lang-toggle { display: flex !important; }
          }
        `}</style>

        <div style={{ maxWidth: 400, width: '100%', padding: '40px' }}>

          {sent ? (
            /* Success State */
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 20 }}>📬</div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: '#111', marginBottom: 12 }}>
                {t(content.checkEmail, lang)}
              </h2>
              <p style={{ color: '#555', fontSize: 15, lineHeight: 1.6, marginBottom: 8 }}>
                {t(content.ifRegistered, lang)}<strong>{email}</strong>{t(content.isRegistered, lang)}
              </p>
              <p style={{ color: '#999', fontSize: 13, lineHeight: 1.6, marginBottom: 32 }}>
                {t(content.expiresIn, lang)}<strong>{t(content.fifteenMin, lang)}</strong>{t(content.checkSpam, lang)}
              </p>
              <Link to="/login" style={{
                display: 'inline-block', background: 'linear-gradient(135deg,#7b1d3a,#c04060)',
                color: '#fff', padding: '12px 32px', borderRadius: 8, textDecoration: 'none',
                fontSize: 14, fontWeight: 600
              }}>
                {t(content.backToLogin, lang)}
              </Link>
            </div>
          ) : (
            /* Form State */
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 40 }}>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: '#111', marginBottom: 8 }}>
                  {t(content.forgotTitle, lang)}
                </h2>
                <p style={{ color: '#9a9a9a', fontSize: 14 }}>
                  {t(content.forgotDesc, lang)}
                </p>
              </div>

              {error && (
                <div style={{
                  background: 'rgba(192, 57, 43, 0.05)', color: '#c0392b',
                  padding: '14px 16px', fontSize: 13, borderLeft: '3px solid #c0392b', marginBottom: 24, borderRadius: '0 8px 8px 0'
                }}>
                  <div>{error}</div>
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(192, 57, 43, 0.15)' }}>
                    <a
                      href="https://wa.me/85266815305?text=Hello%20ETS%20Support,%20I%20need%20help%20resetting%20my%20password."
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#16a34a', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      💬 Contact Admin on WhatsApp for Instant Password Reset →
                    </a>
                  </div>
                </div>
              )}

              <div className="form-group" style={{ marginBottom: 28 }}>
                <label className="form-label-style">{t(content.emailLabel, lang)}</label>
                <input
                  className="form-input-style"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', padding: '14px', justifyContent: 'center' }}
                disabled={loading}
              >
                {loading ? t(content.sending, lang) : t(content.sendLink, lang)}
              </button>

              <div style={{ marginTop: 32, textAlign: 'center', paddingTop: 24, borderTop: '1px solid #eee' }}>
                <Link to="/login" style={{ color: '#9a9a9a', fontSize: 13, textDecoration: 'none' }}>
                  {t(content.backToLogin, lang)}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
